import { Dot } from "lucide-react";
import React, { useContext, useEffect, useRef, useState } from "react";
import Edit from "../../../../assets/images/icons/editIconNew.svg";
import DocumentViewer from "./DocumentViewer";
import MainServices from "../../../../services/MainServices";
import { notifyWarn } from "../../../../assets/elements/Toast";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../context/AuthContext";
import { Field, FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import CustomErrorMessage from "../../../../assets/common/CustomeErrorMessage";
import ButtonLoading from "../../../../assets/loaders/ButtonLoading";
import PDF from "./svg/Pdf";
import AsyncSelect from "react-select/async";
import useRTLContext from "../../../../hooks/useRTLContext";
import { useTranslation } from "react-i18next";
import EditingRestrictionNotice from "./EditRestrictionNotice";
import { documentValidation } from "../employeesModals/validation";

const SubscriptionNew = ({
  subscriptionDetails,
  personalInfo,
  handleGetTableData,
  empPublicId,
  getEmployeeRegistrationData,
  currentEmployeeEditing = false,
}) => {
  const { isRTL, direction } = useRTLContext();
  const { t, i18n } = useTranslation();
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const fileInputRef1Gosi = useRef(null);
  const fileInputRef2Gosi = useRef(null);
  const [fileNames, setFileNames] = useState({
    frontDocument: subscriptionDetails?.frontDocument || "",
    backDocument: subscriptionDetails?.backDocument || "",
    gosiFrontDocument: subscriptionDetails?.gosiFrontDocument || "",
    gosiBackDocument: subscriptionDetails?.gosiBackDocument || "",
  });
  const [initialValues, setInitialValues] = useState({});
  const [editingSections, setEditingSections] = useState({
    subscription: false,
    documents: false,
    gosiDocuments: false,
  });
  const [subscriptionDocsError, setSubscriptionDocsError] = useState(null);
  const [gosiDocsError, setGosiDocsError] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    fileUpload,
    updateEmployeeRegistrationMaster,
    updateSubscriptionInfo,
  } = MainServices();

  /* ---------- onChange ---------- */
  const handleDocumentChange = (e, setFieldValue) => {
    const { name, files } = e.target;
    if (!files || !files[0]) return;

    const fileData = { file: files[0], fileName: files[0].name };

    setFileNames((p) => ({ ...p, [name]: fileData }));

    const map = {
      gosiFrontDocument: "gosiDocFront",
      gosiBackDocument: "gosiDocBack",
      frontDocument: "subscriptionDocFront",
      backDocument: "subscriptionDocBack",
    };

    setFieldValue(map[name], fileData);
  };

  /* ---------- remove ---------- */
  const handleRemoveDocument = (docType, setFieldValue) => {
    setFileNames((p) => ({ ...p, [docType]: "" }));

    const map = {
      gosiFrontDocument: ["gosiDocFront", fileInputRef1Gosi],
      gosiBackDocument: ["gosiDocBack", fileInputRef2Gosi],
      frontDocument: ["subscriptionDocFront", fileInputRef1],
      backDocument: ["subscriptionDocBack", fileInputRef2],
    };

    const [formikField, ref] = map[docType];
    if (ref?.current) ref.current.value = "";
    setFieldValue(formikField, "");
  };

  const handleViewDocument = (documentUrl, documentType, documentLabel) => {
    setSelectedDocument({
      url: documentUrl,
      type: getFileType(documentUrl),
      label: documentLabel,
    });
    setDocumentViewerOpen(true);
  };

  const getFileType = (url) => {
    try {
      const urlObj = new URL(url); // Ensure it's a valid URL
      const pathname = urlObj.pathname;
      const extension = pathname.split(".").pop().toLowerCase();

      return extension || "unknown"; // Return "unknown" if no extension found
    } catch (error) {
      return "invalid URL"; // Handle invalid URLs
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    const updatedSubscriptionInfo = {
      ...(values?.gosiReg && { gosiReg: values.gosiReg }),
      ...{ gosiRegNo: values.gosiRegNo },
      ...(values?.mudadReg && { mudadReg: values.mudadReg }),
      ...{ mudadRegNo: values.mudadRegNo },
    };

    let formattedPersonalInfo = {
      ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
      ...(personalInfo?.secondName && { secondName: personalInfo.secondName }),
      ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
      ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
      ...(personalInfo?.phone && { phone: personalInfo.phone }),
      ...(personalInfo?.email && { email: personalInfo.email }),
      ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
      ...(personalInfo?.dobGregorian && {
        dobGregorian: personalInfo.dobGregorian,
      }),
      ...(personalInfo?.religion && {
        religion: personalInfo.religion,
      }),
      ...(personalInfo?.maritalStatus && {
        maritalStatus: personalInfo.maritalStatus,
      }),
      ...(personalInfo?.gender && { gender: personalInfo.gender }),
      ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

      ...(personalInfo?.citizen === "false" &&
        personalInfo?.nationality && {
          nationality: personalInfo?.nationality,
        }),
      ...(personalInfo?.citizen === "true" &&
        personalInfo?.nationalId && {
          nationalId: personalInfo.nationalId,
        }),
    };

    const details = {
      ...(formattedPersonalInfo && formattedPersonalInfo),
      documents: {
        subscriptionInfo: updatedSubscriptionInfo,
      },
    };
    try {
      const response = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (response?.data) {
        handleEditToggle("subscription");
        setSubscriptionDocsError(null);
        toast("Subscription Details Updated Successfully!", {
          style: { zIndex: 9999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
      } else {
        toast.error("Failed to update passport", {
          style: { zIndex: 9999 },
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "An unexpected error occurred",
        {
          style: { zIndex: 9999 },
        }
      );
      console.error("Error", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDocuments = async (values) => {
    console.log("values: ", values);
    if (
      (values?.mudadReg === "false" || !values?.mudadReg) &&
      (fileNames?.frontDocument || fileNames?.backDocument)
    ) {
      setSubscriptionDocsError(
        "Please complete all required Subscription details before submitting documents"
      );
      return;
    }
    const files = [];

    // Add files only if they exist
    if (fileNames?.frontDocument?.file) {
      files.push(fileNames.frontDocument.file);
    }
    if (fileNames?.backDocument?.file) {
      files.push(fileNames.backDocument.file);
    }

    try {
      let updatedFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle front document
          if (fileNames?.frontDocument?.file) {
            updatedFileNames.frontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedFileNames.frontDocument = fileNames.frontDocument || ""; // Use existing value or empty
          }

          // Handle back document
          if (fileNames?.backDocument?.file) {
            updatedFileNames.backDocument = responseData[currentIndex]?.fileUrl;
          } else {
            updatedFileNames.backDocument = fileNames.backDocument || ""; // Use existing value or empty
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedFileNames = {
          frontDocument: fileNames.frontDocument, // Use existing value or empty
          backDocument: fileNames.backDocument, // Use existing value or empty
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated national ID info
      const updatedSubscriptionInfo = {
        ...(values?.gosiReg && { gosiReg: values.gosiReg }),
        ...{ gosiRegNo: values.gosiRegNo },
        ...(values?.mudadReg && { mudadReg: values.mudadReg }),
        ...{ mudadRegNo: values.mudadRegNo },
        frontDocument: updatedFileNames.frontDocument,
        backDocument: updatedFileNames.backDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          subscriptionInfo: updatedSubscriptionInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("Mudad Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        // Toggle back to "Edit" mode
        handleEditToggle("documents");
      }
    } catch (error) {
      console.error("Error updating national ID information:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  // NEW: Handle GOSI documents update
  const handleUpdateGosiDocuments = async (values) => {
    if (
      (values?.gosiReg === "false" || !values?.gosiReg) &&
      (fileNames?.gosiFrontDocument || fileNames?.gosiBackDocument)
    ) {
      setGosiDocsError(
        "Please complete all required GOSI details before submitting documents"
      );
      return;
    }

    setLoading(true);
    const files = [];

    // Add GOSI files only if they exist
    if (fileNames?.gosiFrontDocument?.file) {
      files.push(fileNames.gosiFrontDocument.file);
    }
    if (fileNames?.gosiBackDocument?.file) {
      files.push(fileNames.gosiBackDocument.file);
    }

    try {
      let updatedGosiFileNames = {};

      // If there are files to upload, call the fileUpload API
      if (files.length > 0) {
        const response = await fileUpload(files);
        if (response?.data) {
          const responseData = response?.data?.data;
          let currentIndex = 0;

          // Handle GOSI front document
          if (fileNames?.gosiFrontDocument?.file) {
            updatedGosiFileNames.gosiFrontDocument =
              responseData[currentIndex]?.fileUrl;
            currentIndex++;
          } else {
            updatedGosiFileNames.gosiFrontDocument =
              fileNames.gosiFrontDocument || "";
          }

          // Handle GOSI back document
          if (fileNames?.gosiBackDocument?.file) {
            updatedGosiFileNames.gosiBackDocument =
              responseData[currentIndex]?.fileUrl;
          } else {
            updatedGosiFileNames.gosiBackDocument =
              fileNames.gosiBackDocument || "";
          }
        } else {
          throw new Error(response?.message || "Unknown error");
        }
      } else {
        // If no files to upload, directly use the existing fileNames values
        updatedGosiFileNames = {
          gosiFrontDocument: fileNames.gosiFrontDocument,
          gosiBackDocument: fileNames.gosiBackDocument,
        };
      }

      // Prepare the personal info object
      let formattedPersonalInfo = {
        ...(personalInfo?.firstName && { firstName: personalInfo.firstName }),
        ...(personalInfo?.secondName && {
          secondName: personalInfo.secondName,
        }),
        ...(personalInfo?.thirdName && { thirdName: personalInfo.thirdName }),
        ...(personalInfo?.lastName && { lastName: personalInfo.lastName }),
        ...(personalInfo?.phone && { phone: personalInfo.phone }),
        ...(personalInfo?.email && { email: personalInfo.email }),
        ...(personalInfo?.dobHijri && { dobHijri: personalInfo.dobHijri }),
        ...(personalInfo?.dobGregorian && {
          dobGregorian: personalInfo.dobGregorian,
        }),
        ...(personalInfo?.religion && { religion: personalInfo.religion }),
        ...(personalInfo?.maritalStatus && {
          maritalStatus: personalInfo.maritalStatus,
        }),
        ...(personalInfo?.gender && { gender: personalInfo.gender }),
        ...(personalInfo?.citizen && { citizen: personalInfo.citizen }),

        ...(personalInfo?.citizen === "false" &&
          personalInfo?.nationality && {
            nationality: personalInfo?.nationality,
          }),
        ...(personalInfo?.citizen === "true" &&
          personalInfo?.nationalId && {
            nationalId: personalInfo.nationalId,
          }),
      };

      // Prepare the updated subscription info with GOSI documents
      const updatedSubscriptionInfo = {
        ...(values?.gosiReg && { gosiReg: values.gosiReg }),
        ...{ gosiRegNo: values.gosiRegNo },
        ...(values?.mudadReg && { mudadReg: values.mudadReg }),
        ...{ mudadRegNo: values.mudadRegNo },
        gosiFrontDocument: updatedGosiFileNames.gosiFrontDocument,
        gosiBackDocument: updatedGosiFileNames.gosiBackDocument,
      };

      // Prepare the final payload
      const details = {
        ...(formattedPersonalInfo && formattedPersonalInfo),
        documents: {
          subscriptionInfo: updatedSubscriptionInfo,
        },
      };

      // Call the update API
      const updateResponse = await updateEmployeeRegistrationMaster(
        details,
        personalInfo?.publicId,
        personalInfo?.concurrencyStamp
      );

      if (updateResponse?.data) {
        toast("GOSI Documents Updated Successfully!", {
          style: { zIndex: 999999999999999 },
        });
        getEmployeeRegistrationData(empPublicId);
        handleGetTableData();
        setGosiDocsError(null);
        // Toggle back to "Edit" mode
        handleEditToggle("gosiDocuments");
      }
    } catch (error) {
      console.error("Error updating GOSI documents:", error);
      notifyWarn(
        error?.response?.data?.message || "An unexpected error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const getValidationSchema = (section) => {
    /* -------- 1. Subscription base fields -------- */
    const baseSchema = {
      gosiReg: Yup.string().required("GOSI Registration is required"),
      gosiRegNo: Yup.string().when("gosiReg", {
        is: "true",
        then: (s) =>
          s
            .required("GOSI Number is required")
            .matches(/^[0-9]+$/, "Only digits allowed")
            .min(4, "At least 4 digits")
            .max(20, "Cannot exceed 20 digits"),
        otherwise: (s) =>
          s.test(
            "empty-when-false",
            "GOSI Number should be empty when GOSI Registration is No",
            (v, ctx) =>
              ctx.parent.gosiReg === "false" ? !v || v.trim() === "" : true
          ),
      }),

      mudadReg: Yup.string().required("Mudad Registration is required"),
      mudadRegNo: Yup.string().when("mudadReg", {
        is: "true",
        then: (s) =>
          s
            .required("Mudad Number is required")
            .matches(/^[0-9]+$/, "Only digits allowed")
            .min(4, "At least 4 digits")
            .max(20, "Cannot exceed 20 digits"),
        otherwise: (s) =>
          s.test(
            "empty-when-false",
            "Mudad Number should be empty when Mudad Registration is No",
            (v, ctx) =>
              ctx.parent.mudadReg === "false" ? !v || v.trim() === "" : true
          ),
      }),
    };

    /* -------- 2.   GOSI docs (2 files) -------- */
    const gosiDocSchema = {
      gosiDocFront: documentValidation(),
      gosiDocBack: documentValidation(),
    };

    /* -------- 3.   Mudad / Subscription docs (2 files) -------- */
    const subscriptionDocSchema = {
      subscriptionDocFront: documentValidation(),
      subscriptionDocBack: documentValidation(),
    };

    switch (section) {
      case "subscription":
        return Yup.object().shape(baseSchema);
      case "gosiDocuments":
        return Yup.object().shape(gosiDocSchema);
      case "documents":
        return Yup.object().shape(subscriptionDocSchema);
      default:
        return Yup.object().shape({});
    }
  };

  const formatDateToYYYYMMDD = (date) => {
    if (!date) return "";

    const isISODate = /^\d{4}-\d{2}-\d{2}T/.test(date);
    const isDDMMYYYY = /^\d{2}\/\d{2}\/\d{4}$/.test(date);
    const isYYYYMMDD = /^\d{4}-\d{2}-\d{2}$/.test(date);

    let formattedDate = "";

    if (isISODate) {
      formattedDate = new Date(date).toISOString().split("T")[0];
    } else if (isDDMMYYYY) {
      const [day, month, year] = date.split("/");
      formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else if (isYYYYMMDD) {
      formattedDate = date;
    }

    return formattedDate;
  };

  function handleOpenFileInput() {
    fileInputRef1?.current?.click();
  }

  function handleOpenFileInput2() {
    fileInputRef2?.current?.click();
  }

  function handleOpenFileInputGosi() {
    fileInputRef1Gosi?.current?.click();
  }

  function handleOpenFileInput2Gosi() {
    fileInputRef2Gosi?.current?.click();
  }

  const formatDate = (dateString) => {
    if (!dateString) return ""; // Handle empty values
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDateToDotFormat = (dateString) => {
    if (!dateString) return ""; // Handle empty or invalid input

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return ""; // Handle empty or undefined values

    const date = new Date(dateString);

    // Convert to YYYY-MM-DD format
    return date.toISOString().split("T")[0];
  };

  const calculateExpiryDays = (dateString) => {
    if (!dateString) return null;
    const expiryDate = new Date(dateString);
    if (isNaN(expiryDate)) return null;
    const diffInMs = expiryDate - new Date();
    const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
    return diffInDays > 0 ? `Expiry In ${diffInDays} Days` : "Expired";
  };

  const handleEditToggle = (section, values) => {
    setEditingSections((prev) => {
      const isCurrentlyOpen = prev[section];
      const newState = {
        subscription: false,
        documents: false,
        gosiDocuments: false,
      };

      // If the clicked section was previously closed, open it
      if (!isCurrentlyOpen) {
        newState[section] = true;
      }

      return newState;
    });

    if (values) {
      setInitialValues(values);
    }
  };

  return (
    <>
      <div className="tab-section">
        {/* Iqama Section */}
        <Formik
          enableReinitialize
          initialValues={{
            gosiRegNo: subscriptionDetails?.gosiRegNo || "",
            gosiReg: subscriptionDetails?.gosiReg || "",
            gosiDocFront: subscriptionDetails?.gosiFrontDocument || "",
            gosiDocBack: subscriptionDetails?.gosiBackDocument || "",
            mudadRegNo: subscriptionDetails?.mudadRegNo || "",
            mudadReg: subscriptionDetails?.mudadReg || "",
            subscriptionDocFront: subscriptionDetails?.frontDocument || "",
            subscriptionDocBack: subscriptionDetails?.backDocument || "",
          }}
          validationSchema={
            editingSections.subscription
              ? getValidationSchema("subscription")
              : editingSections.gosiDocuments
              ? getValidationSchema("gosiDocuments")
              : editingSections.documents
              ? getValidationSchema("documents")
              : undefined
          }
          onSubmit={(values) => {
            if (editingSections.subscription) {
              handleSubmit(values);
            } else if (editingSections.documents) {
              handleUpdateDocuments(values);
            } else if (editingSections.gosiDocuments) {
              handleUpdateGosiDocuments(values);
            }
          }}
        >
          {({ values, errors, touched, setFieldValue, resetForm }) => (
            <>
              <div className="tab-header-container">
                <h3 className="tab-heading">{t("Subscription")}</h3>
                {currentEmployeeEditing ? (
                  <EditingRestrictionNotice message="Read-only" />
                ) : (
                  !editingSections.subscription && (
                    <div
                      className="tab-header-edit-container"
                      onClick={() => handleEditToggle("subscription", values)}
                    >
                      <img src={Edit} alt="edit icon" />
                      <span className="edit-txt">{t("Edit")}</span>
                    </div>
                  )
                )}
              </div>
              <Form className="form-section">
                <div className="add-body-content-grid">
                  {/* GOSI Registration Number */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="gosiRegNo">
                          {t("GOSI Registration Number")}
                        </label>
                        {editingSections.subscription && (
                          <span className="add-req-mark">*</span>
                        )}
                      </div>
                      <div className="add-input-wrapper">
                        {editingSections.subscription ? (
                          <input
                            id="gosiRegNo"
                            name="gosiRegNo"
                            className="add-input"
                            placeholder={t("Enter Registration Number")}
                            value={values.gosiRegNo}
                            onChange={(e) =>
                              setFieldValue("gosiRegNo", e.target.value)
                            }
                          />
                        ) : (
                          <div className="display-value">
                            {values.gosiRegNo || t("N/A")}
                          </div>
                        )}
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="gosiRegNo"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* GOSI Registration */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label">
                          {t("GOSI Registration")}
                        </label>
                        {editingSections.subscription && (
                          <span className="add-req-mark">*</span>
                        )}
                      </div>
                      <div className="add-radio-input-container">
                        <div className="add-radio-container">
                          <input
                            id="gosiYes"
                            type="radio"
                            name="gosiReg"
                            className="add-radio-input"
                            value="true"
                            onChange={(e) =>
                              setFieldValue("gosiReg", e.target.value)
                            }
                            checked={values.gosiReg === "true"}
                            disabled={!editingSections.subscription}
                          />
                          <label htmlFor="gosiYes" className="add-label">
                            {t("Yes")}
                          </label>
                        </div>
                        <div className="add-radio-container">
                          <input
                            id="gosiNo"
                            type="radio"
                            name="gosiReg"
                            className="add-radio-input"
                            value="false"
                            onChange={(e) =>
                              setFieldValue("gosiReg", e.target.value)
                            }
                            checked={values.gosiReg === "false"}
                            disabled={!editingSections.subscription}
                          />
                          <label htmlFor="gosiNo" className="add-label">
                            {t("No")}
                          </label>
                        </div>
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="gosiReg"
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>

                {/* Mudad Registration Number */}
                <div className="add-body-content-grid">
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label" htmlFor="mudadRegNo">
                          {t("Mudad Registration Number")}
                        </label>
                        {editingSections.subscription && (
                          <span className="add-req-mark">*</span>
                        )}
                      </div>
                      <div className="add-input-wrapper">
                        {editingSections.subscription ? (
                          <input
                            id="mudadRegNo"
                            name="mudadRegNo"
                            className="add-input"
                            placeholder={t("Enter Registration Number")}
                            value={values.mudadRegNo}
                            onChange={(e) =>
                              setFieldValue("mudadRegNo", e.target.value)
                            }
                          />
                        ) : (
                          <div className="display-value">
                            {values.mudadRegNo || t("N/A")}
                          </div>
                        )}
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="mudadRegNo"
                      errors={errors}
                      touched={touched}
                    />
                  </div>

                  {/* Mudad Registration */}
                  <div className="form-group">
                    <div className="add-body-input-section">
                      <div className="add-label-container">
                        <label className="add-label">
                          {t("Mudad Registration")}
                        </label>
                        {editingSections.subscription && (
                          <span className="add-req-mark">*</span>
                        )}
                      </div>
                      <div className="add-radio-input-container">
                        <div className="add-radio-container">
                          <input
                            id="mudadYes"
                            type="radio"
                            name="mudadReg"
                            className="add-radio-input"
                            value="true"
                            onChange={(e) =>
                              setFieldValue("mudadReg", e.target.value)
                            }
                            checked={values.mudadReg === "true"}
                            disabled={!editingSections.subscription}
                          />
                          <label htmlFor="mudadYes" className="add-label">
                            {t("Yes")}
                          </label>
                        </div>
                        <div className="add-radio-container">
                          <input
                            id="mudadNo"
                            type="radio"
                            name="mudadReg"
                            className="add-radio-input"
                            value="false"
                            onChange={(e) =>
                              setFieldValue("mudadReg", e.target.value)
                            }
                            checked={values.mudadReg === "false"}
                            disabled={!editingSections.subscription}
                          />
                          <label htmlFor="mudadNo" className="add-label">
                            {t("No")}
                          </label>
                        </div>
                      </div>
                    </div>
                    <CustomErrorMessage
                      name="mudadReg"
                      errors={errors}
                      touched={touched}
                    />
                  </div>
                </div>

                {/* Submit button for Subscription section (only visible in edit mode) */}
                {editingSections.subscription && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => {
                          resetForm({ values: initialValues });
                          handleEditToggle("subscription");
                        }}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Documents Section Header */}
                <div className="tab-header-container">
                  <h3 className="tab-heading">{t("GOSI Documents")}</h3>
                  {currentEmployeeEditing ? (
                    <EditingRestrictionNotice message="View-only" />
                  ) : (
                    !editingSections.gosiDocuments && (
                      <div
                        className="tab-header-edit-container"
                        onClick={() => handleEditToggle("gosiDocuments")}
                      >
                        <img src={Edit} alt="edit icon" />
                        <span className="edit-txt">{t("Edit")}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Documents Section Body */}
                {editingSections.gosiDocuments ? (
                  // Edit Mode
                  <div className="upload-body-tab">
                    {gosiDocsError && (
                      <div className="details-error">{gosiDocsError}</div>
                    )}
                    {/* Front Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="subscriptionDocFront"
                            className="upload-label"
                          >
                            {t("GOSI Front Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInputGosi}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames?.gosiFrontDocument?.fileName ||
                                fileNames?.gosiFrontDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="gosiFrontDocument"
                              ref={fileInputRef1Gosi}
                              id="gosiDocFront"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.gosiFrontDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "gosiFrontDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="gosiDocFront"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label htmlFor="gosiDocBack" className="upload-label">
                            {t("Back Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput2Gosi}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames?.gosiBackDocument?.fileName ||
                                fileNames?.gosiBackDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="gosiBackDocument"
                              ref={fileInputRef2Gosi}
                              id="gosiDocBack"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.gosiBackDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "gosiBackDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="gosiDocBack"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                ) : (
                  // Non-Edit Mode
                  <div className="upload-body-tab">
                    {/* Front Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="gosiDocFront"
                            className="upload-label"
                          >
                            {t("GOSI Front Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.gosiDocFront
                                ? t("GOSI Front Document")
                                : t(t("No Document Uploaded"))}
                            </span>
                          </div>
                          {values.gosiDocFront && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.gosiDocFront,
                                  "GOSI Front Document",
                                  "GOSI Front"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label htmlFor="gosiDocBack" className="upload-label">
                            {t("Back Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.gosiDocBack
                                ? t("GOSI Back Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.gosiDocBack && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.gosiDocBack,
                                  "GOSI Back Document",
                                  "GOSI Back"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit button for Documents section (only visible in edit mode) */}
                {editingSections.gosiDocuments && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => handleEditToggle("gosiDocuments")}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}

                {/* Documents Section Header */}
                <div className="tab-header-container">
                  <h3 className="tab-heading">{t("Mudad Documents")}</h3>
                  {currentEmployeeEditing ? (
                    <EditingRestrictionNotice message="View-only" />
                  ) : (
                    !editingSections.documents && (
                      <div
                        className="tab-header-edit-container"
                        onClick={() => handleEditToggle("documents")}
                      >
                        <img src={Edit} alt="edit icon" />
                        <span className="edit-txt">{t("Edit")}</span>
                      </div>
                    )
                  )}
                </div>

                {/* Documents Section Body */}
                {editingSections.documents ? (
                  // Edit Mode
                  <div className="upload-body-tab">
                    {subscriptionDocsError && (
                      <div className="details-error">
                        {subscriptionDocsError}
                      </div>
                    )}
                    {/* Front Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="subscriptionDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames?.frontDocument?.fileName ||
                                fileNames?.frontDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="frontDocument"
                              ref={fileInputRef1}
                              id="subscriptionDocFront"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.frontDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "frontDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="subscriptionDocFront"
                        errors={errors}
                        touched={touched}
                      />
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container m-0">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="subscriptionDocBack"
                            className="upload-label"
                          >
                            {t("Back Document")}
                          </label>
                          <span className="upload-req-mark">*</span>
                        </div>
                        <div className="remove-btn-wrapper">
                          <div
                            className="upload-file-input-wrapper"
                            onClick={handleOpenFileInput2}
                          >
                            <span className="upload-file-input-replacer">
                              {fileNames?.backDocument?.fileName ||
                                fileNames?.backDocument ||
                                t("No Document")}
                            </span>
                            <input
                              name="backDocument"
                              ref={fileInputRef2}
                              id="subscriptionDocBack"
                              type="file"
                              className="upload-file-hidden-input"
                              onChange={(e) =>
                                handleDocumentChange(e, setFieldValue)
                              }
                            />
                            <button type="button" className="upload-btn">
                              {t("Upload File")}
                            </button>
                          </div>
                          {fileNames?.backDocument && (
                            <button
                              type="button"
                              className="remove-file-btn-national"
                              onClick={() =>
                                handleRemoveDocument(
                                  "backDocument",
                                  setFieldValue
                                )
                              }
                            >
                              {t("Remove")}
                            </button>
                          )}
                        </div>
                      </div>
                      <CustomErrorMessage
                        name="subscriptionDocBack"
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  </div>
                ) : (
                  // Non-Edit Mode
                  <div className="upload-body-tab">
                    {/* Front Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="subscriptionDocFront"
                            className="upload-label"
                          >
                            {t("Front Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.subscriptionDocFront
                                ? t("Front Document")
                                : t(t("No Document Uploaded"))}
                            </span>
                          </div>
                          {values.subscriptionDocFront && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.subscriptionDocFront,
                                  "Front Document",
                                  "Subscription Front"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Back Document */}
                    <div className="upload-file-input-container-tab">
                      <div className="upload-file-input-container2">
                        <div className="upload-label-container">
                          <label
                            htmlFor="subscriptionDocBack"
                            className="upload-label"
                          >
                            {t("Back Document")}
                          </label>
                        </div>
                        <div className="document-display-container-tab">
                          <div className="document-info-tab">
                            <PDF />
                            <span className="document-name-tab">
                              {values.subscriptionDocBack
                                ? t("Back Document")
                                : t("No Document Uploaded")}
                            </span>
                          </div>
                          {values.subscriptionDocBack && (
                            <button
                              type="button"
                              className="view-document-btn-tab"
                              onClick={() =>
                                handleViewDocument(
                                  values.subscriptionDocBack,
                                  "Back Document",
                                  "Subscription Back"
                                )
                              }
                            >
                              {t("View")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit button for Documents section (only visible in edit mode) */}
                {editingSections.documents && (
                  <div className="add-footer">
                    <span></span>
                    <div className="add-footer-right">
                      <button
                        type="button"
                        className="add-footer-cancel-btn"
                        onClick={() => handleEditToggle("documents")}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="add-footer-submit-btn"
                        disabled={loading}
                      >
                        {loading ? <ButtonLoading /> : t("Submit")}
                      </button>
                    </div>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      </div>

      <DocumentViewer
        isOpen={documentViewerOpen}
        onClose={() => setDocumentViewerOpen(false)}
        documentUrl={selectedDocument?.url}
        documentType={selectedDocument?.type}
        documentLabel={selectedDocument?.label}
      />
    </>
  );
};

export default SubscriptionNew;
